import { t } from "i18next";
import moment from "moment";
import { utils, write } from "xlsx";
import { BseDto, ReceivedBseBidDto } from "../../../../../autogen/bff-api";
import { ProductGroup } from "../../../../../common/types";
import { generateUniqueExcelSheetName, sanitizeFilename } from "../../../../../common/utils/sanitizeUtils";
import { findProductBid } from "./BidEvaluationUtils";

type Cell = string | number | boolean | undefined | null;

export interface PriceComparisonSheet {
  name: string;
  rows: Cell[][];
}

/**
 * Builds one sheet per product group where every supplier's unit price is laid out
 * side by side (one column per supplier) on the same product row, prefixed with the
 * procurement reference and download timestamp. Missing prices are left blank, never 0.
 */
export const buildPriceComparisonSheets = (
  productGroups: ProductGroup[],
  bids: ReceivedBseBidDto[],
  sourcingEvent: BseDto,
  downloadedAt: string
): PriceComparisonSheet[] => {
  const usedSheetNames = new Set<string>();

  return productGroups.map((group) => {
    const buyerFields = group.productFields.filter((f) => f.populatedBy === "Buyer");

    const metaRows: Cell[][] = [
      [t("Procurement"), sourcingEvent.title],
      ...(sourcingEvent.referenceId ? [[t("Reference"), sourcingEvent.referenceId]] : []),
      [t("Downloaded at"), downloadedAt],
      [],
    ];

    const headings: Cell[] = [
      ...buyerFields.map((field) => field.name),
      t("Quantity"),
      ...bids.map((bid) => bid.owningOrganization.name),
    ];

    const productRows: Cell[][] = (group.products ?? []).map((product) => [
      ...buyerFields.map((field) => product.productFields.find((f) => f.id === field.id)?.value ?? undefined),
      product.quantity.value ?? undefined,
      ...bids.map((bid) => findProductBid({ productId: product.id, groupId: group.id, bid })?.unitPrice.value ?? undefined),
    ]);

    return {
      name: generateUniqueExcelSheetName(group.name, usedSheetNames),
      rows: [...metaRows, headings, ...productRows],
    };
  });
};

/**
 * Generates and triggers download of an Excel file comparing all suppliers' unit prices
 * across every product in the sourcing event. Used by the "lowest price" evaluation model.
 */
export const generatePriceComparisonReport = (
  productGroups: ProductGroup[],
  bids: ReceivedBseBidDto[] | undefined,
  sourcingEvent: BseDto
) => {
  if (!productGroups?.length || !bids?.length)
    throw Error("Could not generate price comparison. No product groups or bids found.");

  const downloadedAt = moment().format("DD.MM.YYYY HH:mm");
  const sheets = buildPriceComparisonSheets(productGroups, bids, sourcingEvent, downloadedAt);

  const workbook = utils.book_new();
  for (const sheet of sheets) {
    utils.book_append_sheet(workbook, utils.aoa_to_sheet(sheet.rows), sheet.name);
  }

  const arrayBuffer = write(workbook, { bookType: "xlsx", type: "buffer" });
  const blob = new Blob([arrayBuffer], { type: "application/octet-stream" });
  const url = window.URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = `${sanitizeFilename(sourcingEvent.title)}_${sanitizeFilename(t("Unit Price"))}.xlsx`;

  a.click();

  window.URL.revokeObjectURL(url);
};
